/**
 * OAuth utilities for n450s frontend
 * 
 * supports PKCE flow for public clients
 */

import { AUTH_URL } from "../config/env";
import { clearAuthTokens } from "./authTokens";
import { getOidcConfiguration, getFallbackEndpoints } from "../services/oidcDiscovery";
import { toSameOriginPath } from "./sameOriginPath";

const AUTH_BASE_URL = AUTH_URL;
const CLIENT_ID = 'n450s_web';

// generate random string for state/verifier
function generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => charset[byte % charset.length]).join('');
}

// generate code challenge from verifier (S256)
async function generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);

    // base64url encode
    const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
    return base64
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export interface LoginOptions {
    redirectUri?: string;
    usePkce?: boolean;
    prompt?: 'none';
}

/**
 * redirect to auth service login page
 */
export async function redirectToLogin(options: LoginOptions = {}): Promise<void> {
    const { redirectUri, usePkce = true, prompt } = options;

    // Get OIDC configuration (with fallback)
    let authEndpoint: string;
    try {
        const config = await getOidcConfiguration();
        authEndpoint = config.authorization_endpoint;
    } catch (error) {
        console.warn('[OAuth] Failed to fetch OIDC config, using fallback:', error);
        const fallback = getFallbackEndpoints();
        authEndpoint = fallback.authorization_endpoint;
    }

    // remember where to land afterwards. SessionManager sends the user to
    // /login?return=<path> when their session dies mid-page; without stashing it
    // here the callback has no idea and always dumped them on the home page.
    const returnParam = new URLSearchParams(window.location.search).get('return');
    const returnTo = toSameOriginPath(
        returnParam ?? `${window.location.pathname}${window.location.search}`,
    );
    if (returnTo && !returnTo.startsWith('/login')) {
        sessionStorage.setItem('oauth_return_to', returnTo);
    }

    // generate state for CSRF protection
    const state = generateRandomString(32);
    sessionStorage.setItem('oauth_state', state);

    // generate nonce for ID token validation
    const nonce = generateRandomString(32);
    sessionStorage.setItem('oauth_nonce', nonce);

    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: redirectUri || `${window.location.origin}/auth/callback`,
        response_type: 'code',
        scope: 'openid profile email backend:read backend:write',
        state,
        nonce, // Add nonce to authorization request
    });

    if (prompt) {
        params.set('prompt', prompt);
    }

    // add PKCE if enabled
    if (usePkce) {
        const verifier = generateRandomString(64);
        sessionStorage.setItem('pkce_verifier', verifier);

        const challenge = await generateCodeChallenge(verifier);
        params.set('code_challenge', challenge);
        params.set('code_challenge_method', 'S256');
    }

    // Use discovered authorization endpoint
    window.location.href = `${authEndpoint}?${params.toString()}`;
}

/**
 * redirect to auth service signup page
 */
export async function redirectToSignup(options: LoginOptions = {}): Promise<void> {
    const { redirectUri, usePkce = true, prompt } = options;

    // generate state for CSRF protection
    const state = generateRandomString(32);
    sessionStorage.setItem('oauth_state', state);

    // generate nonce for ID token validation
    const nonce = generateRandomString(32);
    sessionStorage.setItem('oauth_nonce', nonce);

    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: redirectUri || `${window.location.origin}/auth/callback`,
        response_type: 'code',
        scope: 'openid profile email backend:read backend:write',
        state,
        nonce, // Add nonce to authorization request
    });

    if (prompt) {
        params.set('prompt', prompt);
    }

    // add PKCE if enabled
    if (usePkce) {
        const verifier = generateRandomString(64);
        sessionStorage.setItem('pkce_verifier', verifier);

        const challenge = await generateCodeChallenge(verifier);
        params.set('code_challenge', challenge);
        params.set('code_challenge_method', 'S256');
    }

    // save params and redirect to signup
    const oauthParams = {
        client_id: CLIENT_ID,
        redirect_uri: redirectUri || `${window.location.origin}/auth/callback`,
        response_type: 'code',
        scope: 'openid profile email backend:read backend:write',
        state,
        ...(params.get('code_challenge') ? {
            code_challenge: params.get('code_challenge'),
            code_challenge_method: 'S256',
        } : {}),
    };

    // redirect to signup with OAuth params encoded
    // Note: signup endpoint is not part of OIDC discovery, use base URL
    window.location.href = `${AUTH_BASE_URL}/signup?redirect=${encodeURIComponent(JSON.stringify(oauthParams))}`;
}

/**
 * logout - clear tokens and optionally redirect to auth service
 */
export function logout(redirectToAuth: boolean = false): void {
    // clear local tokens
    clearAuthTokens();
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('pkce_verifier');
    sessionStorage.removeItem('oauth_return_to');

    if (redirectToAuth) {
        // RP-initiated logout, not the bare /logout page. Only this endpoint
        // ends the IdP's own SSO session; without it the next sign-in is
        // auto-approved with no password prompt, which reads as "logout did
        // nothing". The site header already does it this way.
        const postLogout = encodeURIComponent(window.location.origin);
        window.location.href = `${AUTH_BASE_URL}/oauth/logout?client_id=${CLIENT_ID}&post_logout_redirect_uri=${postLogout}`;
    }
}

/**
 * get auth service URL for external use
 */
export function getAuthServiceUrl(): string {
    return AUTH_BASE_URL;
}

/**
 * check if currently on auth callback page
 */
export function isAuthCallback(): boolean {
    return window.location.pathname === '/auth/callback' ||
        window.location.pathname === '/oauth/callback';
}

/*
 * attemptSilentAuth() used to live here: a hidden iframe pointed at the IdP's
 * /oauth/consent with prompt=none. It could never have worked — the auth service
 * sends `X-Frame-Options: SAMEORIGIN` and a CSP with `frame-ancestors 'self'`,
 * so the iframe was blocked and the function silently fell through its own
 * 5-second timeout and reported failure. Nothing referenced it.
 *
 * Silent re-authentication is handled server-side instead: the backend BFF holds
 * the refresh token and /api/auth/session renews the access token without the
 * browser touching the IdP at all. If a cross-document silent flow is ever
 * needed, it has to be a top-level redirect, not a frame.
 * (security audit 2026-08, F-20260801-21)
 */
